import React, { useState } from 'react';
import { X, Users, ArrowRight, MinusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { addPlatformTeamSeat, removePlatformTeamSeat } from '../lib/platformTeamSeats';

const EXTRA_SEAT_PRICE_LABEL =
  (typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_PLATFORM_EXTRA_TEAM_SEAT_PRICE_LABEL) ||
  '$9.99/mo';

export interface ResourceUpgradeUsage {
  active: number;
  limit: number;
  planIncluded: number;
  purchasedAdditional: number;
  isUnlimited: boolean;
}

interface ResourceUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  businessId: string | null | undefined;
  /** Only the business owner can change paid seat quantity (Stripe is on the owner account). */
  isBusinessOwner: boolean;
  currentUsage?: ResourceUpgradeUsage;
}

export default function ResourceUpgradeModal({
  isOpen,
  onClose,
  onSuccess,
  businessId,
  isBusinessOwner,
  currentUsage,
}: ResourceUpgradeModalProps) {
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(false);

  if (!isOpen) return null;

  const usage = currentUsage;
  const limitLabel =
    usage?.isUnlimited === true ? '∞' : String(usage?.limit ?? '—');
  const purchased = usage?.purchasedAdditional ?? 0;
  const planIncluded = usage?.planIncluded ?? 0;

  const handleAddPaidSeat = async () => {
    if (!businessId) {
      toast.error('No business selected');
      return;
    }
    setAdding(true);
    try {
      const result = await addPlatformTeamSeat(businessId);
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      toast.success('Extra team seat added. You can add another team member now.');
      onSuccess?.();
      onClose();
    } finally {
      setAdding(false);
    }
  };

  const handleRemovePaidSeat = async () => {
    if (!businessId) {
      toast.error('No business selected');
      return;
    }
    if (purchased < 1) return;
    setRemoving(true);
    try {
      const result = await removePlatformTeamSeat(businessId);
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      toast.success('Extra team seat removed from your subscription.');
      onSuccess?.();
      onClose();
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
        aria-hidden
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 bg-gray-900 rounded-t-xl">
            <h2 className="text-lg font-semibold text-white">Team member limit reached</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1 hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-400 hover:text-white" />
            </button>
          </div>

          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-blue-600" />
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2">Add capacity</h3>

            <p className="text-gray-600 mb-4 leading-relaxed text-left text-sm">
              Your plan includes a set number of team members. You can upgrade to a higher tier
              for more included seats, or add paid extra seats on your current plan. Extra seats
              are billed separately in Stripe and stay on your subscription until you remove them
              or change plans.
            </p>

            {usage && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-left text-sm text-gray-700 space-y-1">
                <div>
                  <span className="font-medium">Active team members:</span>{' '}
                  {usage.active}
                  {usage.isUnlimited ? '' : ` / ${limitLabel} allowed`}
                </div>
                {!usage.isUnlimited && (
                  <>
                    <div>
                      <span className="font-medium">Included with plan:</span> {planIncluded}
                    </div>
                    <div>
                      <span className="font-medium">Paid extra seats:</span> {purchased}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="space-y-3">
              {isBusinessOwner && !usage?.isUnlimited && (
                <button
                  type="button"
                  onClick={handleAddPaidSeat}
                  disabled={adding || !businessId}
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adding ? 'Processing…' : `Add 1 extra seat (${EXTRA_SEAT_PRICE_LABEL})`}
                </button>
              )}

              {!isBusinessOwner && !usage?.isUnlimited && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-left">
                  Only the business owner can purchase or remove extra seats. Ask the owner to add
                  capacity or upgrade the plan.
                </p>
              )}

              {isBusinessOwner && purchased > 0 && !usage?.isUnlimited && (
                <button
                  type="button"
                  onClick={handleRemovePaidSeat}
                  disabled={removing || !businessId}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                >
                  <MinusCircle className="h-4 w-4" />
                  {removing ? 'Processing…' : 'Remove 1 paid extra seat'}
                </button>
              )}

              <Link
                to="/my-apps"
                onClick={onClose}
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                View plans & upgrade
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>

              <button
                type="button"
                onClick={onClose}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
